// frontend/src/components/PlayerDistribution.jsx
// Dual bell-curve visualization: player vs own season + player vs league
import { useState, useMemo } from 'react';
import { BarChart3 } from 'lucide-react';
import BellCurveChart from './BellCurveChart';
import {
  computeMeanStdDev,
  LEAGUE_AVERAGES,
} from '../services/nbaApi';

// League season distributions — computed from 46,150 game-log rows across
// 509 NBA players with ≥10 games played, 2024-25 season.
const LEAGUE_SEASON_DISTRIBUTIONS = {
  pts:  { mean: 9.481,  stdDev: 6.513 },
  reb:  { mean: 3.777,  stdDev: 2.390 },
  ast:  { mean: 2.194,  stdDev: 1.820 },
  stl:  { mean: 0.703,  stdDev: 0.404 },
  blk:  { mean: 0.424,  stdDev: 0.400 },
  fg3m: { mean: 1.117,  stdDev: 0.894 },
};

// ─── Stat definitions ───────────────────────────────────────────────────────
const STAT_BUTTONS = [
  { key: 'pts',  label: 'Points',   shortLabel: 'PTS', color: '#f97316' },
  { key: 'reb',  label: 'Rebounds', shortLabel: 'REB', color: '#3b82f6' },
  { key: 'ast',  label: 'Assists',  shortLabel: 'AST', color: '#22c55e' },
  { key: 'stl',  label: 'Steals',   shortLabel: 'STL', color: '#06b6d4' },
  { key: 'blk',  label: 'Blocks',   shortLabel: 'BLK', color: '#ef4444' },
  { key: 'fg3m', label: '3PT Made', shortLabel: '3PM', color: '#eab308' },
];

// ─── Main Component ─────────────────────────────────────────────────────────

const PlayerDistribution = ({ player, gameLogs }) => {
  const [activeStat, setActiveStat] = useState('pts');
  const [viewMode, setViewMode] = useState('last_game'); // 'last_game' | 'full_season'

  const logs = useMemo(() => gameLogs || [], [gameLogs]);

  // ─── Compute game-log distributions ────────────────────────────────────
  const distributions = useMemo(() => {
    if (logs.length < 3) return null;
    const result = {};
    STAT_BUTTONS.forEach(({ key }) => {
      const values = logs.map((g) => {
        const v = g[key];
        if (v === undefined || v === null) return 0;
        const n = Number(v);
        return isNaN(n) ? 0 : n;
      });
      const { mean, stdDev } = computeMeanStdDev(values);
      result[key] = { mean, stdDev, values };
    });
    return result;
  }, [logs]);

  // Most recent game
  const latestGame = logs.length > 0 ? logs[0] : null;

  const activeConfig = STAT_BUTTONS.find((s) => s.key === activeStat);
  const playerDist   = distributions?.[activeStat];
  const leagueDist   = LEAGUE_AVERAGES[activeStat];           // per-game league avg (game-level)
  const leagueSeasonDist = LEAGUE_SEASON_DISTRIBUTIONS[activeStat]; // season-avg distribution

  // ── Last Game mode: the value to mark ────────────────────────────────
  let currentGameValue = null;
  if (latestGame) {
    const raw = latestGame[activeStat];
    if (raw !== undefined && raw !== null) {
      const parsed = Number(raw);
      currentGameValue = isNaN(parsed) ? null : parsed;
    }
  }

  // ── Full Season mode: player's season-to-date average from game logs ──
  // Computed directly from gameLogs so it always works even without the
  // paid /season_averages endpoint. seasonAvg from the API is used as a
  // fallback label only.
  const playerSeasonValue = useMemo(() => {
    if (logs.length === 0) return null;
    const values = logs.map((g) => {
      const v = g[activeStat];
      if (v === undefined || v === null) return null;
      const n = Number(v);
      return isNaN(n) ? null : n;
    }).filter((v) => v !== null);
    if (values.length === 0) return null;
    return values.reduce((sum, v) => sum + v, 0) / values.length;
  }, [logs, activeStat]);

  if (!player) return null;

  const playerName  = `${player.first_name} ${player.last_name}`;
  const idSeed      = `${player.id}-${activeStat}-${viewMode}`;

  const canRenderLastGame   = playerDist && currentGameValue !== null && leagueDist;
  const canRenderFullSeason = leagueSeasonDist && playerSeasonValue !== null;

  const isLastGame   = viewMode === 'last_game';
  const isFullSeason = viewMode === 'full_season';

  return (
    <div className="bg-gray-800/50 rounded-2xl border border-gray-700/50 overflow-hidden">
      {/* Header */}
      <div className="px-6 pt-6 pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-6 w-6 text-orange-500" />
            <h4 className="text-xl font-bold text-white">Distribution Analysis</h4>
          </div>

          {/* Last Game / Full Season toggle */}
          <div className="flex bg-gray-700/50 rounded-lg p-1 gap-1">
            <button
              onClick={() => setViewMode('last_game')}
              className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                isLastGame ? 'bg-orange-500 text-white' : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              Last Game
            </button>
            <button
              onClick={() => setViewMode('full_season')}
              className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-colors ${
                isFullSeason ? 'bg-orange-500 text-white' : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              Full Season
            </button>
          </div>
        </div>

        <p className="text-gray-400 text-sm mt-2">
          {isLastGame
            ? `Where ${playerName}'s most recent game falls on their season distribution and vs the league.`
            : `Where ${playerName}'s season average ranks among all NBA players with 10+ games played.`}
        </p>
      </div>

      {/* ── Stat Selector ─────────────────────────────────────────────── */}
      <div className="px-6 pb-4">
        <div className="flex flex-wrap gap-2">
          {STAT_BUTTONS.map(({ key, shortLabel, color }) => {
            const isActive = activeStat === key;
            return (
              <button
                key={key}
                onClick={() => setActiveStat(key)}
                className="relative px-5 py-2.5 rounded-xl text-sm font-bold transition-all duration-200"
                style={{
                  backgroundColor: isActive ? `${color}22` : 'rgba(55,65,81,0.4)',
                  border: `2px solid ${isActive ? color : 'rgba(75,85,99,0.4)'}`,
                  color: isActive ? color : '#9ca3af',
                  boxShadow: isActive ? `0 0 16px ${color}33` : 'none',
                }}
              >
                {shortLabel}
                {isActive && (
                  <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Visualizations ────────────────────────────────────────────── */}
      <div className="px-6 pb-6">

        {/* ══ LAST GAME MODE ══ */}
        {isLastGame && (
          <>
            {logs.length < 3 && (
              <p className="text-gray-500 text-center py-8">
                Need at least 3 games to build a distribution. {logs.length} game(s) available.
              </p>
            )}
            {logs.length >= 3 && !canRenderLastGame && (
              <p className="text-gray-500 text-center py-8">
                No data for {activeConfig.label} in the most recent game.
              </p>
            )}
            {canRenderLastGame && (
              <div className="space-y-8">
                {/* Stat highlight */}
                <div
                  className="flex items-center gap-4 p-4 rounded-xl"
                  style={{ backgroundColor: `${activeConfig.color}11`, border: `1px solid ${activeConfig.color}33` }}
                >
                  <div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider">Most Recent Game</div>
                    <div className="text-3xl font-black mt-1" style={{ color: activeConfig.color }}>{currentGameValue}</div>
                    <div className="text-sm text-gray-400 mt-0.5">{activeConfig.label}</div>
                  </div>
                  <div className="ml-auto text-right">
                    <div className="text-xs text-gray-400">Season Avg</div>
                    <div className="text-lg font-bold text-gray-300">{playerDist.mean.toFixed(1)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-400">League Avg</div>
                    <div className="text-lg font-bold text-gray-300">{leagueDist.mean.toFixed(1)}</div>
                  </div>
                </div>

                {/* Curve 1: vs Own Season (empirical KDE) */}
                <BellCurveChart
                  key={`player-${idSeed}`}
                  mean={playerDist.mean}
                  stdDev={playerDist.stdDev}
                  values={playerDist.values}
                  playerValue={currentGameValue}
                  label="vs Own Season"
                  gradientId={`grad-player-${idSeed}`}
                  accentColor={activeConfig.color}
                  height={210}
                />

                {/* Curve 2: vs League per-game (theoretical) */}
                <BellCurveChart
                  key={`league-${idSeed}`}
                  mean={leagueDist.mean}
                  stdDev={leagueDist.stdDev}
                  playerValue={currentGameValue}
                  label="vs League (per game)"
                  gradientId={`grad-league-${idSeed}`}
                  accentColor="#a855f7"
                  height={210}
                />

                {/* Legend */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-gray-700/30 rounded-xl p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="h-3 w-3 rounded-full" style={{ backgroundColor: activeConfig.color }} />
                      <span className="text-gray-300 font-medium">vs Own Season</span>
                    </div>
                    <p className="text-gray-500 text-xs">
                      Empirical KDE from {logs.length} game{logs.length !== 1 ? 's' : ''} with minutes played.
                      Mean: {playerDist.mean.toFixed(1)}, SD: {playerDist.stdDev.toFixed(2)}
                    </p>
                  </div>
                  <div className="bg-gray-700/30 rounded-xl p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="h-3 w-3 rounded-full bg-purple-500" />
                      <span className="text-gray-300 font-medium">vs League</span>
                    </div>
                    <p className="text-gray-500 text-xs">
                      NBA-wide per-game averages for qualified players.
                      Mean: {leagueDist.mean.toFixed(1)}, SD: {leagueDist.stdDev.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ══ FULL SEASON MODE ══ */}
        {isFullSeason && (
          <>
            {!canRenderFullSeason && (
              <p className="text-gray-500 text-center py-8">
                {!leagueSeasonDist
                  ? 'League season distribution unavailable.'
                  : `No season average available for ${playerName}.`}
              </p>
            )}
            {canRenderFullSeason && (
              <div className="space-y-8">
                {/* Season avg highlight */}
                <div
                  className="flex items-center gap-4 p-4 rounded-xl"
                  style={{ backgroundColor: `${activeConfig.color}11`, border: `1px solid ${activeConfig.color}33` }}
                >
                  <div>
                    <div className="text-xs text-gray-400 uppercase tracking-wider">Season Average</div>
                    <div className="text-3xl font-black mt-1" style={{ color: activeConfig.color }}>
                      {playerSeasonValue.toFixed(1)}
                    </div>
                    <div className="text-sm text-gray-400 mt-0.5">{activeConfig.label} per game</div>
                  </div>
                  <div className="ml-auto text-right">
                    <div className="text-xs text-gray-400">League Avg</div>
                    <div className="text-lg font-bold text-gray-300">{leagueSeasonDist.mean.toFixed(1)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-400">Std Dev</div>
                    <div className="text-lg font-bold text-gray-300">{leagueSeasonDist.stdDev.toFixed(2)}</div>
                  </div>
                </div>

                {/* Curve: Player season avg vs all-player season-avg distribution */}
                <BellCurveChart
                  key={`season-dist-${idSeed}`}
                  mean={leagueSeasonDist.mean}
                  stdDev={leagueSeasonDist.stdDev}
                  playerValue={playerSeasonValue}
                  label="vs All NBA Players (≥10 games, season averages)"
                  gradientId={`grad-season-${idSeed}`}
                  accentColor={activeConfig.color}
                  height={210}
                />

                {/* Legend */}
                <div className="bg-gray-700/30 rounded-xl p-3 text-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="h-3 w-3 rounded-full" style={{ backgroundColor: activeConfig.color }} />
                    <span className="text-gray-300 font-medium">Season Average Distribution</span>
                  </div>
                  <p className="text-gray-500 text-xs">
                    Distribution of per-game season averages across all NBA players with 10+ games played in 2024-25.
                    The marker shows where {playerName}'s {playerSeasonValue.toFixed(1)} {activeConfig.label.toLowerCase()} per game ranks.
                    League mean: {leagueSeasonDist.mean.toFixed(1)}, SD: {leagueSeasonDist.stdDev.toFixed(2)}.
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PlayerDistribution;
